/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/button-has-type */
/* eslint-disable default-case */
/* eslint-disable no-alert */
import React, {
  useEffect, useRef, useState, useMemo, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';

import CustomLayout from './components/presentational/CustomLayout';
import FeedBoxContent from './components/presentational/FeedBoxContent';
import PinnedFeedBoxContent from './components/presentational/PinnedFeedBoxContent';
import FeedHeader from './components/presentational/FeedHeader';
import TotalPrsWidget from './components/presentational/TotalPrsWidget';
import ActionButton from '../../shared/components/ActionButton/ActionButton';
import ModalHandler from './ModalHandler';
import SubModalHandler from './SubModalHandler';
import LoadMoreWrapper from './components/presentational/LoadMoreWrapper';
import TopPerformanceWidgetsContainer from './components/presentational/TopPerformanceWidgetsContainer';
import AddPrWidgetButton from './components/presentational/AddPrWidgetButton';
import SelectedGroups from './components/presentational/SelectedGroups';
import StatusIndicator from './components/presentational/StatusIndicator';
import NewPRButton from './components/presentational/NewPRButton';
import SocketHandler from './SocketHandler';

import {
  fetchFeedPRCount,
  setActiveReduxModal,
  setIsFeedShowing,
} from './ducks/feedActions';
import useFeed from './components/hooks/useFeed';
import { showInfoToast } from '../../shared/utils/toastManager';
import { textSlicer, transformPostText } from './helperFunctions';

const CreateButtonWrapper = styled('div')`
  position: fixed;
  bottom: 50px;
  right: 50px;
  @media screen and (max-width: 768px) {
    right: 20px;
  }
`;

const FeedContainer = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 67%;
  max-width: 80%;
  padding-top: 10px;
  padding-right: 15px;
  align-items: center;
  @media screen and (max-width: 932px) {
    width: 90%;
    margin: 0 auto;
    padding-right: 0;
  }
`;

const FeedContent = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: ${(props) => (props.isTvMode ? '450px' : '600px')};
  align-items: center;
  @media screen and (max-width: 640px) {
    min-width: ${(props) => (props.isTvMode ? '450px' : 'unset')};
  }
`;

const InnerComponents = styled('div')`
  display: flex;
  justify-content: center;
  flex-direction: ${(props) => (props.isTvMode ? 'row' : 'column')};
  align-items: ${(props) => (props.isTvMode ? 'left' : 'center')};
  width: 100%;
`;

const SelectedGroupsBar = styled('div')`
  width: ${(props) => (props.isTvMode ? '100%' : '600px')};
  margin: 0 auto 8px auto;
  min-width: 450px;
`;

const WidgetContainerWrapper = styled('div')`
  position: relative;
  max-width: 33%;
  min-width: 450px;
  padding-top: 10px;
  padding-left: 15px;
  @media screen and (max-width: 1200px) {
    min-width: unset;
    width: 300px;
  }
  @media screen and (max-width: 932px) {
    display: none;
  }
`;

const WidgetsContainer = styled('div')`
  position: sticky;
  top: 70px;
  left: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 92vh;
  overflow: auto;
`;

const Feed = () => {
  const { handleFetchFeed } = useFeed();

  const dispatch = useDispatch();
  const topRef = useRef();
  const pinnedFeedTopRef = useRef();
  const [displayTotalPrsWidget, setDisplayTotalPrsWidget] = useState(true);

  const isTvMode = useSelector((state) => state.feed.data.isTvMode);
  const feedPRCount = useSelector((state) => state.feed.data.feedPRCount);
  const selectedGroups = useSelector((state) => state.feed.data.selectedGroups);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isFeedShowing = useSelector((state) => state.feed.data.isFeedShowing);
  const pinnedFeed = useSelector((state) => state.feed.data.pinnedFeed);
  const isPinnedFeedLoading = useSelector(
    (state) => state.feed.ui.isFeedLoading,
  );
  const newIncomingFeedPost = useSelector(
    (state) => state.feed.data.newIncomingFeedPost,
  );
  const firstPostId = useSelector((state) => state.feed.data.firstPostId);
  const topPrWidgets = useSelector((state) => state.feed.data.topPrWidgets);
  const showPinnedFeed = !!pinnedFeed?.length && !isPinnedFeedLoading && isFeedShowing;

  const handleSetActiveModal = useCallback((activeModalString) => {
    dispatch(setActiveReduxModal(activeModalString));
  }, [dispatch]);

  const handleCreatePostClick = useCallback(() => {
    handleSetActiveModal('create-post-modal');
  }, [handleSetActiveModal]);

  const scrollToTopOfFeed = useCallback(() => {
    if (showPinnedFeed && !isTvMode) {
      pinnedFeedTopRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showPinnedFeed, isTvMode]);

  const handleIncomingFeedPost = useCallback(() => {
    if (newIncomingFeedPost && Object.keys(newIncomingFeedPost).length) {
      if (!isTvMode) {
        const toastId = showInfoToast(
          `New post by ${newIncomingFeedPost.userName}`,
          textSlicer(transformPostText(newIncomingFeedPost.postText), 70),
          () => {
            if (!isFeedShowing) {
              dispatch(setIsFeedShowing(true));
            }
            scrollToTopOfFeed();
            toast.dismiss(toastId);
          },
        );
      }
      if (isTvMode && isFeedShowing) {
        scrollToTopOfFeed();
      }
    }
  }, [newIncomingFeedPost, isTvMode, isFeedShowing, dispatch, scrollToTopOfFeed]);

  useEffect(() => {
    handleIncomingFeedPost();
  }, [handleIncomingFeedPost]);

  useEffect(() => {
    if (!isFeedShowing && firstPostId) {
      scrollToTopOfFeed();
    }
  }, [isFeedShowing, firstPostId, scrollToTopOfFeed]);

  useEffect(() => {
    // update PR count & PR posts every 30 seconds to account for new data
    const interval = setInterval(() => {
      dispatch(fetchFeedPRCount(currentUser.accountCode));
      if (!isFeedShowing) {
        handleFetchFeed(currentUser.accountCode);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [currentUser, dispatch, isFeedShowing]);

  const renderNewPRButton = useMemo(() => (!feedPRCount ? null : <NewPRButton />), [feedPRCount]);

  const renderSelectedGroupsSection = useMemo(() => selectedGroups.length > 0 && (
    <SelectedGroupsBar isTvMode={isTvMode}>
      <SelectedGroups />
    </SelectedGroupsBar>
  ), [selectedGroups.length, isTvMode]);

  const renderTvModeContent = useMemo(() => (
    <>
      <FeedContainer>
        {renderSelectedGroupsSection}
        <FeedContent isTvMode={isTvMode}>
          <FeedBoxContent
            isTvMode={isTvMode}
            topRef={topRef}
            scrollToTopOfFeed={scrollToTopOfFeed}
          />
          <LoadMoreWrapper />
        </FeedContent>
      </FeedContainer>
      <WidgetContainerWrapper>
        <WidgetsContainer>
          {displayTotalPrsWidget && (
            <TotalPrsWidget
              displayWidget={displayTotalPrsWidget}
              setDisplayWidget={setDisplayTotalPrsWidget}
            />
          )}
          <TopPerformanceWidgetsContainer />
          {topPrWidgets.length < 5 && <AddPrWidgetButton />}
        </WidgetsContainer>
      </WidgetContainerWrapper>
      <StatusIndicator />
    </>
  ), [
    renderSelectedGroupsSection,
    isTvMode,
    displayTotalPrsWidget,
    topPrWidgets.length,
    scrollToTopOfFeed,
  ]);

  const renderBaseFeedContent = useMemo(() => (
    <>
      <FeedHeader scrollToTopOfFeed={scrollToTopOfFeed} />
      <FeedContent isTvMode={isTvMode}>
        {renderSelectedGroupsSection}
        {renderNewPRButton}
        {showPinnedFeed && <PinnedFeedBoxContent pinnedFeedTopRef={pinnedFeedTopRef} />}
        <FeedBoxContent isTvMode={isTvMode} topRef={topRef} scrollToTopOfFeed={scrollToTopOfFeed} />
        <LoadMoreWrapper />
      </FeedContent>
      <StatusIndicator />
      <CreateButtonWrapper>
        <ActionButton icon='plus' onClick={handleCreatePostClick} />
      </CreateButtonWrapper>
    </>
  ), [
    scrollToTopOfFeed,
    isTvMode,
    renderSelectedGroupsSection,
    renderNewPRButton,
    showPinnedFeed,
    handleCreatePostClick,
  ]);

  return (
    <CustomLayout isTvMode={isTvMode} scrollToTopOfFeed={scrollToTopOfFeed}>
      <div>
        <Helmet>
          <title>Team Feed | TeamBuildr</title>
        </Helmet>
        <InnerComponents isTvMode={isTvMode}>
          {isTvMode ? renderTvModeContent : renderBaseFeedContent}
        </InnerComponents>
        <ModalHandler />
        <SubModalHandler />
      </div>
      <SocketHandler />
    </CustomLayout>
  );
};

export default Feed;
